import { PageType } from '@vgtv/api-client/lib/page';
import { Provider } from '@vgtv/api-client';

import { PageTypeAdditional } from '../../../types.js';
import { config } from '../../../config.js';
import { getHomepageSlug } from '../../../shared/helpers/getHomepageSlug.js';
import { getCategoryChain } from '../../../shared/helpers/getCategoryChain.js';
import { getEmbeddedTags } from '../../../shared/helpers/assetHelpers.js';

import type { SvpCategoryAdditional } from '@vgtv/api-client/lib/svp_category';
import type { CnpTag } from '@vgtv/api-client/lib/cnp_tag';
import type { CnpStory } from '@vgtv/api-client/lib/cnp_story';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { Vendor } from '../../../types.js';
import type { SvpAssetWithEmbedded } from '../../../shared/helpers/assetHelpers.js';
import type { SDRN } from './getSDRN.js';

export interface PageEntityMap {
  [PageTypeAdditional.ASSET]: SvpAssetAdditional;
  [PageType.CATEGORY]: SvpCategoryAdditional;
  [PageType.TAG]: CnpTag;
  [PageType.STORY]: CnpStory;
  [PageType.CUSTOM]: {
    slug: string;
  };
}

export type TrackablePageType = keyof PageEntityMap;

type TrackablePage = {
  [K in keyof PageEntityMap]: {
    pageType: K;
    vendor: Vendor;
    data: PageEntityMap[K];
    isSalesposter?: boolean;
  };
}[keyof PageEntityMap];

let getPageObjectPromise: ReturnType<typeof getPageObjectAsync>;

const getPageObjectId = ({
  type,
  vendor,
  id,
}: {
  type: string;
  vendor: Vendor;
  id: string | number;
}) => {
  const pulseSite =
    vendor === 'brandstudio' ? 'brandstudio' : config.pulse.site;

  return `sdrn:${pulseSite}:${type}:${id}`.toLowerCase() as SDRN;
};

const getPageObjectAsync = async ({
  pageType,
  vendor,
  data,
  isSalesposter,
}: TrackablePage) => {
  if (pageType === PageType.CUSTOM) {
    if (data.slug === getHomepageSlug(config.provider)) {
      const type = config.provider === Provider.AB ? 'Listing' : 'Frontpage';

      return {
        '@type': type,
        '@id': getPageObjectId({ type, vendor, id: '/' }),
        name: data.slug,
      };
    } else {
      const type = 'Listing';

      return {
        '@type': type,
        '@id': getPageObjectId({ type, vendor, id: data.slug }),
        name: data.slug,
      };
    }
  }

  if (pageType === PageType.CATEGORY) {
    const type = 'Listing';

    return {
      '@type': type,
      '@id': getPageObjectId({ type, vendor, id: 'category' }),
      category: data.title.toLowerCase(),
      name: data.title,
    };
  }

  if (pageType === PageType.STORY || pageType === PageType.TAG) {
    const type = 'Listing';

    return {
      '@type': type,
      '@id': getPageObjectId({ type, vendor, id: pageType }),
      name: data.title,
    };
  }

  if (pageType === PageTypeAdditional.ASSET) {
    let type;
    if (!isSalesposter) {
      type = config.provider === Provider.AB ? 'Article' : 'Video';
    } else {
      type = 'SalesPoster';
    }

    const category =
      config.provider === Provider.AB
        ? (await getCategoryChain(data.category.id))
            .join(' > ')
            .replace(/^webbtv/, 'video')
        : data.category.title.toLowerCase();

    const tags = getEmbeddedTags(data as SvpAssetWithEmbedded).map(
      ({ tag }) => tag
    );

    if (vendor === 'brandstudio') {
      // used for ATE-based retargeting
      tags.push(window.location.href.replace(/\?.*/, ''));
      tags.push(window.location.pathname);
    }

    return {
      '@type': type,
      '@id': getPageObjectId({ type, vendor, id: data.id }),
      contentId:
        config.provider === Provider.VGTV ? String(data.id) : undefined,
      category,
      name: data.title,
      tags,
      ...(isSalesposter &&
        config.provider === Provider.AB && {
          salesPosterType: 'Paid content',
          target: {
            '@id': `sdrn:aftonbladet:mediaasset:${data.id}`,
            '@type': 'MediaAsset',
            name: data.title,
          },
        }),
    };
  }

  return { type: 'Page' };
};

export const getPageObject = async (options: TrackablePage) => {
  getPageObjectPromise = getPageObjectAsync(options);

  return getPageObjectPromise;
};

export const getCurrentPageObject = async () => getPageObjectPromise;
